import { loginUser, submitOtp } from "../../../Redux/Auth/AuthActions";
import { AppDispatch, RootState } from "../../../Redux/store";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../Common/Loading/Loading";
import React, { useEffect, useState } from "react";
import validator from "../../../Util/validator";
import Modal from "../../Common/Modal/Modal";
import styles from "./OtpModal.module.scss";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  email: string;
  password: string;
  openPasswordModalHandler: () => void;
}

function OtpModal(props: Props) {
  const { isOpen, setIsOpen, email, password, openPasswordModalHandler } =
    props;

  const dispatch = useDispatch<AppDispatch>();

  const { submitOtpLoading, loading } = useSelector(
    (state: RootState) => state.auth,
  );

  const [otpCode, setOtpCode] = useState("");

  const [errors, setErrors] = useState<{
    otpCode: string;
  }>({
    otpCode: "",
  });

  const OTP_DURATION = 5 * 60;

  const [timeLeft, setTimeLeft] = useState(OTP_DURATION);
  const [timerExpired, setTimerExpired] = useState(false);

  const startTimer = () => {
    setTimeLeft(OTP_DURATION);
    setTimerExpired(false);
  };

  useEffect(() => {
    if (!isOpen) {
      setOtpCode("");
      return
    };

    startTimer();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (timerExpired) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setTimerExpired(true);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timerExpired, timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const submitOtpHandler = () => {
    const { errors: fieldErrors, isValid } = validator([
      {
        key: "otpCode",
        type: "numberOnly",
        value: otpCode,
        minLength: 6,
      },
    ]);

    setErrors({ ...errors, ...fieldErrors });

    if (isValid) {
      if (!submitOtpLoading) {
        const successHandler = () => {
          setIsOpen(false);
          setOtpCode("");
        };

        dispatch(
          submitOtp(
            { otpCode, email },
            openPasswordModalHandler,
            successHandler,
          ),
        );
      }
    }
  };

  const resendOtpHandler = () => {
    if (!loading && timerExpired) {
      dispatch(loginUser({ email, password }));
      startTimer();
    }
  };

  return (
    <React.Fragment>
      {submitOtpLoading && <Loading />}

      <Modal isOpen={isOpen} setIsOpen={setIsOpen} showCloseIcon={false}>
        <div className={styles.mainContainer}>
          <div className={styles.headingContainer}>
            <h1 className={styles.heading}>Authenticate</h1>
          </div>

          <div className={styles.mainBodyContainer}>
            <div className={styles.description}>
              Please enter the OTP sent to your email to continue
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>OTP</p>

              <input
                className={styles.input}
                type="text"
                value={otpCode}
                onChange={(e) => {
                  if (
                    /^\d*$/.test(e.target.value) &&
                    e.target.value.length <= 6
                  ) {
                    setOtpCode(e.target.value);
                  }
                }}
              />

              <p className={styles.error}>{errors?.otpCode}</p>
            </div>

            <div className={styles.timerContainer}>
              <button
                className={styles.resendButton}
                onClick={resendOtpHandler}
                disabled={!timerExpired || loading}
              >
                Resend
              </button>

              {!timerExpired && (
                <p className={styles.timer}>{formatTime(timeLeft)}</p>
              )}
            </div>

            <div className={styles.buttonContainer}>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setOtpCode("");
                  setIsOpen(false);
                  setErrors({ ...errors, otpCode: "" });
                }}
              >
                Cancel
              </button>

              <button
                className={styles.updateButton}
                onClick={submitOtpHandler}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </React.Fragment>
  );
}

export default OtpModal;
